/**
 * Offline API Layer — wraps fetch with IndexedDB cache fallback.
 *
 * Online:  fetch → response + write to IndexedDB
 * Offline: read from IndexedDB cache
 *
 * Used by TanStack Query queryFn to enable offline-first data access.
 * Mutations go through BackgroundSync (handled by SW, not this layer).
 *
 * Security: Cache keys include tenant ID to prevent cross-tenant data leakage.
 * Cleanup: clearOfflineCache() must be called on logout (GDPR compliance).
 */

import { useAuthStore } from '@/stores/auth-store';

const OFFLINE_DB_NAME = 'kgc-offline-api';
const OFFLINE_DB_VERSION = 1;
const STORE_NAME = 'api-cache';

interface CacheEntry {
  key: string; // tenantId::url (tenant-isolated)
  url: string;
  tenantId: string;
  data: unknown;
  cachedAt: number;
  ttl: number;
}

// TTL defaults per API prefix (milliseconds)
const TTL_MAP: Record<string, number> = {
  '/api/v1/products-direct': 24 * 60 * 60 * 1000, // 24h
  '/api/v1/partners-direct': 24 * 60 * 60 * 1000, // 24h
  '/api/v1/rental-equipment': 24 * 60 * 60 * 1000, // 24h
  '/api/v1/rentals': 60 * 60 * 1000, // 1h
  '/api/v1/worksheets': 60 * 60 * 1000, // 1h
  '/api/v1/invoices': 60 * 60 * 1000, // 1h
  '/api/v1/cash': 60 * 60 * 1000, // 1h
};

const DEFAULT_TTL = 60 * 60 * 1000; // 1h fallback

function getTTL(url: string): number {
  for (const [prefix, ttl] of Object.entries(TTL_MAP)) {
    if (url.startsWith(prefix)) return ttl;
  }
  return DEFAULT_TTL;
}

/** Get current tenant ID from auth store (for cache key isolation) */
function getCurrentTenantId(): string {
  const user = useAuthStore.getState().user as { tenantId?: string } | null;
  return user?.tenantId ?? 'default';
}

/** Build tenant-isolated cache key */
function cacheKey(url: string): string {
  return `${getCurrentTenantId()}::${url}`;
}

// Singleton IDB connection (Fix #5: prevents connection leak)
let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null; // Reset on failure so retry works
      reject(request.error);
    };
  });
  return dbPromise;
}

async function getCached<T>(url: string): Promise<T | undefined> {
  try {
    const db = await getDB();
    return new Promise(resolve => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(cacheKey(url));
      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined;
        if (!entry) {
          resolve(undefined);
          return;
        }
        const isExpired = Date.now() - entry.cachedAt > entry.ttl;
        resolve(isExpired ? undefined : (entry.data as T));
      };
      request.onerror = () => resolve(undefined);
    });
  } catch {
    return undefined;
  }
}

async function setCache(url: string, data: unknown): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const entry: CacheEntry = {
      key: cacheKey(url),
      url,
      tenantId: getCurrentTenantId(),
      data,
      cachedAt: Date.now(),
      ttl: getTTL(url),
    };
    store.put(entry);
  } catch {
    // Silent fail — cache write is best-effort
  }
}

/** Validate response is JSON before caching (Fix #6: prevents cache poisoning) */
function isJsonResponse(response: Response): boolean {
  const ct = response.headers.get('content-type');
  return ct != null && ct.includes('application/json');
}

/**
 * Offline-aware fetch wrapper for GET requests.
 * Online: fetches from network and caches the response.
 * Offline: returns cached data if available.
 *
 * Returns the HTTP status code alongside data so the caller can handle 401.
 */
export async function offlineFetch<T>(url: string, init?: RequestInit): Promise<T> {
  // Only cache GET requests
  const method = init?.method?.toUpperCase() ?? 'GET';
  if (method !== 'GET') {
    const response = await fetch(url, init);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json() as Promise<T>;
  }

  // Online path: fetch + cache
  if (navigator.onLine) {
    try {
      const response = await fetch(url, init);

      // Fix #3: Re-throw 401 so ApiClient.request() can handle token refresh
      if (response.status === 401) {
        throw new OfflineLayerHttpError(401, 'Unauthorized');
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // M-028 fix: Check Content-Type BEFORE consuming body to prevent caching non-JSON
      if (!isJsonResponse(response)) {
        // Non-JSON response (e.g. HTML error page from proxy) — do not cache
        const text = await response.text();
        try {
          return JSON.parse(text) as T;
        } catch {
          throw new Error(`Expected JSON response but got ${response.headers.get('content-type')}`);
        }
      }
      const data = (await response.json()) as T;
      void setCache(url, data);
      return data;
    } catch (error) {
      // Don't fallback to cache for auth errors
      if (error instanceof OfflineLayerHttpError) throw error;
      // Network error while "online" (e.g. server down) — try cache
      const cached = await getCached<T>(url);
      if (cached !== undefined) return cached;
      throw error;
    }
  }

  // Offline path: serve from cache
  const cached = await getCached<T>(url);
  if (cached !== undefined) return cached;
  throw new Error('Offline — nincs gyorsítótárazott adat ehhez a kéréshez.');
}

/** Custom error for HTTP status codes that need special handling */
export class OfflineLayerHttpError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'OfflineLayerHttpError';
  }
}

/**
 * Prefetch critical data for offline use.
 * Call this when the app starts (online) to warm the cache.
 *
 * TODO(M-027): navigator.onLine is unreliable on captive portals and some mobile browsers.
 * Consider adding: (1) online/offline event listeners to trigger prefetch on reconnect,
 * (2) heartbeat ping to /api/v1/health, (3) max cache entry size limit (5MB),
 * (4) total cache quota check to prevent unbounded growth.
 */
export async function prefetchForOffline(
  endpoints: string[],
  headers?: HeadersInit
): Promise<void> {
  if (!navigator.onLine) return;

  await Promise.allSettled(
    endpoints.map(async url => {
      try {
        const response = await fetch(url, { headers });
        if (response.ok && isJsonResponse(response)) {
          const data: unknown = await response.json();
          await setCache(url, data);
        }
      } catch {
        // Silent — prefetch is best-effort
      }
    })
  );
}

/**
 * Clear all offline cached data. MUST be called on logout.
 * Fix #2: Prevents cross-user data leakage (GDPR compliance).
 */
export async function clearOfflineCache(): Promise<void> {
  try {
    // Close singleton connection first
    if (dbPromise) {
      const db = await dbPromise;
      db.close();
      dbPromise = null;
    }
    indexedDB.deleteDatabase(OFFLINE_DB_NAME);
  } catch {
    // Best-effort cleanup
  }
}

/** Critical endpoints to prefetch for offline (ADR-002 matrix) */
export const CRITICAL_OFFLINE_ENDPOINTS = [
  '/api/v1/products-direct',
  '/api/v1/partners-direct',
  '/api/v1/rental-equipment',
] as const;
